import { Button, Card, Col, Form, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FC, useEffect, useMemo, useState } from "react";
import Flatpickr from "react-flatpickr";
import { useParams } from "react-router-dom";
import moment from "moment";

import TableContainer from "Common/TableContainer";
import TableEntry from "Common/TableEntry";
import { ListPagination } from "Common/ListPagination";
import { settingServices } from "Utils/setting/services";
import { currentDateTime, getStartTimeOfDay } from "Utils/datefilter";

// Mocked types — replace these with real ones
interface StatementListData {
    domainName: string;
    date: string;
    ipAddress: string;
    geolocation?: {
        ip?: string;
        country?: string;
        state?: string;
        district?: string;
        city?: string;
        isp?: string;
        zipcode?: string;
    };
    changed_by_user_name?: string;
}
interface ProfitLossTotal {
    total: number;
    page: number;
}

interface Props {
    activeTab: string;
    filter: boolean;
}

const ChangePasswordHistory: FC<Props> = ({ activeTab, filter }) => {
    const today = new Date();
    const initialStartDate = getStartTimeOfDay(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
    const initialEndDate = currentDateTime();

    const [statementData, setStatementData] = useState<StatementListData[]>([]);
    const [startDate, setStartDate] = useState<Date | string>(initialStartDate);
    const [endDate, setEndDate] = useState<Date | string>(initialEndDate);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [limit, setLimit] = useState<number>(50);
    const [page, setPage] = useState<number>(1);
    const [listTotal, setListTotal] = useState<ProfitLossTotal>({ total: 0, page: 1 });

    const { userid } = useParams();

    const getPasswordChangedHistory = async (filters: any = {}) => {
        try {
            setIsLoading(true);
            const { response } = await settingServices.getPasswordChangedHistory({
                limit: filters.limit || limit,
                page: filters.page || page,
                user_id: userid || "",
                from_date: filters.startDate || startDate,
                to_date: filters.endDate || endDate,
            });

            if (response?.data) {
                setStatementData(response?.data || []);
                const metadata = response?.data?.metadata?.[0] || {};
                setListTotal({
                    total: metadata?.total || 0,
                    page: metadata?.page || 1,
                });
            } else {
                setStatementData([]);
                setListTotal({ total: 0, page: 1 });
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === "ChangePasswordHistory") {
            getPasswordChangedHistory({ limit, page });
        }
    }, [activeTab]);

    const handlePageFilter = (page: number, limit: number) => {
        setPage(page);
        setLimit(limit);
    };

    const handleFilterSubmit = () => {
        setPage(1);
        getPasswordChangedHistory({ limit, page: 1, startDate, endDate });
    };

    const handleClearSelection = () => {
        const resetStart = getStartTimeOfDay(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
        const resetEnd = currentDateTime();
        setStartDate(resetStart);
        setEndDate(resetEnd);
        setPage(1);
        getPasswordChangedHistory({ limit, page: 1, startDate: resetStart, endDate: resetEnd });
    };

    const columns = useMemo(() => [
        {
            header: "No.",
            accessorKey: "no",
            cell: (cell: any) => cell.row.index + 1,
        },
        {
            header: "User Name",
            accessorKey: "domainName",
            cell: (cell: any) => (
                <span className="cursor-pointer text-info">
                    {cell?.row?.original?.changed_by_user_name}
                </span>
            ),
        },
        {
            header: "Date",
            accessorKey: "date",
            cell: (cell: any) => (
                <div>
                    <p className="mb-0">
                        {moment(cell?.row?.original?.date).format("DD-MM-YY")}
                    </p>
                    <p className="mb-0">
                        {moment(cell?.row?.original?.date).format("hh:mm:ss:SSS A")}
                    </p>
                </div>
            ),
        },
        {
            header: "IP Address",
            accessorKey: "ipAddress",
            cell: (cell: any) => {
                const geo = cell?.row?.original?.geolocation;
                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id={`tooltip-ip-${cell.row.index}`}>
                                <div style={{ whiteSpace: 'pre-line' }}>
                                    <strong>IP:</strong> {geo?.ip || 'N/A'}{"\n"}
                                    <strong>Country:</strong> {geo?.country || 'N/A'}{"\n"}
                                    <strong>State:</strong> {geo?.state || 'N/A'}{"\n"}
                                    <strong>District:</strong> {geo?.district || 'N/A'}{"\n"}
                                    <strong>City:</strong> {geo?.city || 'N/A'}{"\n"}
                                    <strong>ISP:</strong> {geo?.isp || 'N/A'}{"\n"}
                                    <strong>Zip:</strong> {geo?.zipcode || 'N/A'}
                                </div>
                            </Tooltip>
                        }
                    >
                        <span className="text-primary cursor-pointer">
                            {geo?.ip || cell?.row?.original?.ip_address || 'N/A'}
                        </span>
                    </OverlayTrigger>
                );
            },
        },
    ], []);

    return (
        <>
            {filter && (
                <Card>
                    <Card.Body>
                        <Form>
                            <Row className="gy-3 align-items-end">
                                <Col lg={9}>
                                    <Row className="gap-3 gap-sm-0">
                                        <Col lg={4}>
                                            <Form.Label>Start Date</Form.Label>
                                            <Flatpickr
                                                className="form-control"
                                                options={{
                                                    enableTime: true,
                                                    dateFormat: "Y-m-d H:i:S",
                                                    maxDate: endDate,
                                                }}
                                                value={startDate}
                                                onChange={([date]: [Date]) => setStartDate(date)}
                                            />
                                        </Col>
                                        <Col lg={4}>
                                            <Form.Label>End Date</Form.Label>
                                            <Flatpickr
                                                className="form-control"
                                                options={{
                                                    enableTime: true,
                                                    dateFormat: "Y-m-d H:i:S",
                                                    maxDate: currentDateTime(),
                                                }}
                                                value={endDate}
                                                onChange={([date]: [Date]) => setEndDate(date)}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                                <Col lg={3}>
                                    <Button
                                        className="me-2 btn-sm"
                                        style={{ height: "38px" }}
                                        onClick={handleFilterSubmit}
                                    >
                                        <i className="fs-lg align-middle ri-filter-line"></i> Submit
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        className="btn-sm"
                                        style={{ height: "38px" }}
                                        onClick={handleClearSelection}
                                    >
                                        <i className="fs-lg align-middle ri-refresh-line"></i> Clear
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
            )}

            <Card>
                <Card.Body>
                    <TableContainer
                        columns={columns}
                        data={statementData}
                        isBordered={false}
                        customPageSize={50}
                        isPagination={false}
                        tableClass="table-centered align-middle table-nowrap table-striped mb-3"
                        theadClass="text-muted table-light"
                        SearchPlaceholder="Search History..."
                    />
                    <nav
                        aria-label="Page navigation"
                        className="px-3 d-flex flex-column flex-sm-row align-items-center"
                    >
                        <TableEntry
                            limit={limit}
                            handlelimitFilter={(value: number) => handlePageFilter(1, value)}
                        />
                        <ListPagination
                            listTotal={listTotal}
                            handlePageFilter={(value: number) => handlePageFilter(value, limit)}
                        />
                    </nav>
                </Card.Body>
            </Card>

            {isLoading && <div className="text-center my-3">Loading...</div>}
        </>
    );
};

export default ChangePasswordHistory;
